import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, FileVideo, FileImage, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

const getFileIcon = (file) => {
  if (!file) return FileText;
  const type = file.type || '';
  if (type.startsWith('video/')) return FileVideo;
  if (type.startsWith('image/')) return FileImage;
  return FileText;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

async function uploadChunked(file, folder, onProgress) {
  // Initiate multipart
  const initForm = new FormData();
  initForm.append('file_name', file.name);
  initForm.append('folder', folder);
  const initRes = await axios.post(`${API}/api/upload/initiate-multipart`, initForm);
  const { upload_id, s3_key } = initRes.data;

  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const parts = [];
  let uploaded = 0;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    const chunkForm = new FormData();
    chunkForm.append('file', chunk, `chunk_${i + 1}`);
    chunkForm.append('upload_id', upload_id);
    chunkForm.append('s3_key', s3_key);
    chunkForm.append('part_number', String(i + 1));

    const partRes = await axios.post(`${API}/api/upload/upload-chunk`, chunkForm);
    parts.push(partRes.data);
    uploaded += (end - start);
    onProgress(Math.round((uploaded / file.size) * 100));
  }

  // Complete
  const completeForm = new FormData();
  completeForm.append('upload_id', upload_id);
  completeForm.append('s3_key', s3_key);
  completeForm.append('parts_json', JSON.stringify(parts));
  completeForm.append('original_filename', file.name);
  const completeRes = await axios.post(`${API}/api/upload/complete-multipart`, completeForm);
  return completeRes.data;
}

async function uploadDirect(file, uploadUrl, fieldName = 'file', extraFields = {}, onProgress) {
  const form = new FormData();
  form.append(fieldName, file);
  Object.entries(extraFields).forEach(([k, v]) => form.append(k, v));
  const res = await axios.post(uploadUrl, form, {
    onUploadProgress: (e) => {
      if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return res.data;
}

/**
 * BulkFileUploader - reusable multi-file uploader with chunked support for large files.
 *
 * Props:
 *   accept        - file input accept string, e.g. "image/*,video/*,.pdf"
 *   multiple      - allow multiple files (default true)
 *   uploadUrl     - direct upload endpoint (for small files)
 *   folder        - S3 folder for chunked uploads (default "uploads")
 *   fieldName     - form field name for direct upload (default "file")
 *   extraFields   - extra form fields to include in direct upload
 *   chunkThreshold - size in bytes above which chunked upload is used (default 10MB)
 *   onUploadComplete - callback(results[]) after all files uploaded
 *   onFileUploaded   - callback(result, file) after each individual file
 *   label         - label text
 *   description   - helper text
 *   maxFiles      - max number of files (0 = unlimited)
 *   disabled      - disable the uploader
 *   compact       - compact mode (smaller UI)
 */
export function BulkFileUploader({
  accept = '*/*',
  multiple = true,
  uploadUrl,
  folder = 'uploads',
  fieldName = 'file',
  extraFields = {},
  chunkThreshold = 10 * 1024 * 1024,
  onUploadComplete,
  onFileUploaded,
  label = 'Upload Files',
  description = '',
  maxFiles = 0,
  disabled = false,
  compact = false,
}) {
  const [files, setFiles] = useState([]); // { id, file, status, progress, error, result }
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const addFiles = useCallback((newFiles) => {
    const fileArray = Array.from(newFiles);
    const toAdd = maxFiles > 0 ? fileArray.slice(0, maxFiles - files.length) : fileArray;
    const entries = toAdd.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f,
      status: 'pending',
      progress: 0,
      error: null,
      result: null,
    }));
    setFiles((prev) => [...prev, ...entries]);
  }, [files.length, maxFiles]);

  const removeFile = useCallback((id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (disabled || uploading) return;
    addFiles(e.dataTransfer.files);
  }, [disabled, uploading, addFiles]);

  const uploadAll = useCallback(async () => {
    setUploading(true);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      const entry = files[i];
      if (entry.status === 'done') { results.push(entry.result); continue; }
      if (entry.status === 'error') continue;

      setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: 'uploading', progress: 0 } : f));

      try {
        let result;
        if (entry.file.size > chunkThreshold) {
          result = await uploadChunked(entry.file, folder, (pct) => {
            setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, progress: pct } : f));
          });
        } else if (uploadUrl) {
          result = await uploadDirect(entry.file, uploadUrl, fieldName, extraFields, (pct) => {
            setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, progress: pct } : f));
          });
        } else {
          // Fallback: use chunked for everything if no direct URL
          result = await uploadChunked(entry.file, folder, (pct) => {
            setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, progress: pct } : f));
          });
        }
        setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: 'done', progress: 100, result } : f));
        results.push(result);
        onFileUploaded?.(result, entry.file);
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Upload failed';
        setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: 'error', error: msg } : f));
      }
    }

    setUploading(false);
    onUploadComplete?.(results);
  }, [files, chunkThreshold, folder, uploadUrl, fieldName, extraFields, onFileUploaded, onUploadComplete]);

  const pendingCount = files.filter((f) => f.status === 'pending' || f.status === 'error').length;

  return (
    <div className="space-y-3" data-testid="bulk-file-uploader">
      {/* Drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors
          ${disabled || uploading ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/30'}
          ${compact ? 'p-3' : 'p-6'}`}
        data-testid="bulk-upload-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          disabled={disabled || uploading}
          data-testid="bulk-upload-input"
        />
        <Upload className={`mx-auto text-gray-400 ${compact ? 'w-5 h-5' : 'w-8 h-8 mb-2'}`} />
        <p className={`font-medium text-gray-700 ${compact ? 'text-xs' : 'text-sm'}`}>{label}</p>
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
        {!compact && <p className="text-xs text-gray-400 mt-1">Drag & drop or click to browse. Large videos are uploaded in chunks automatically.</p>}
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="bulk-upload-file-list">
          {files.map((entry) => {
            const Icon = getFileIcon(entry.file);
            return (
              <div key={entry.id} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2 text-sm" data-testid={`upload-file-${entry.id}`}>
                <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="truncate text-gray-800 text-xs font-medium">{entry.file.name}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{formatSize(entry.file.size)}</span>
                    {entry.status === 'uploading' && (
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-orange-500 rounded-full transition-all duration-300" style={{ width: `${entry.progress}%` }} />
                      </div>
                    )}
                    {entry.status === 'uploading' && <span className="text-xs text-orange-600 font-medium">{entry.progress}%</span>}
                    {entry.status === 'done' && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                    {entry.status === 'error' && <span className="text-xs text-red-500 truncate">{entry.error}</span>}
                  </div>
                </div>
                {(entry.status === 'pending' || entry.status === 'error') && !uploading && (
                  <button onClick={(e) => { e.stopPropagation(); removeFile(entry.id); }} className="text-gray-400 hover:text-red-500" data-testid={`remove-file-${entry.id}`}>
                    <X className="w-4 h-4" />
                  </button>
                )}
                {entry.status === 'uploading' && <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload button */}
      {pendingCount > 0 && (
        <Button
          onClick={uploadAll}
          disabled={uploading || disabled}
          className="w-full bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="bulk-upload-submit"
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> Upload {pendingCount} file{pendingCount !== 1 ? 's' : ''}</>
          )}
        </Button>
      )}
    </div>
  );
}

export default BulkFileUploader;
